import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { craftsmanService, Craftsman } from '../../services/craftsmanService';
import { traderService, TraderFeatures } from '../../services/traderService';
import { triggerKeywordGeneration } from '../../services/keywordService';
import { Phone, Globe, MapPin, Info, Edit2, Check, X, Upload, Store, User, RefreshCw } from 'lucide-react';
import { supabase } from '../../config/supabase';
import SocialLinksCard from '../../components/dashboard/SocialLinksCard';
import { compressImage, AVATAR_PRESET, GALLERY_PRESET, formatFileSize } from '../../utils/imageCompression';

const AvatarUpload = ({
  crafty,
  onAvatarUpdate
}: {
  crafty: Craftsman;
  onAvatarUpdate: (url: string) => void;
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !crafty.id) return;

    try {
      const originalSize = file.size;
      const compressedFile = await compressImage(file, AVATAR_PRESET);
      const compressedSize = compressedFile.size;
      const reduction = originalSize > 0
        ? ((originalSize - compressedSize) / originalSize * 100).toFixed(0)
        : '0';

      const avatarUrl = await craftsmanService.uploadAvatar(crafty.id, compressedFile);
      await craftsmanService.updateCraftsman(crafty.id, { avatar_url: avatarUrl });
      onAvatarUpdate(avatarUrl);
      alert(
        `Avatar updated successfully. Optimized: ${formatFileSize(originalSize)} → ${formatFileSize(compressedSize)} (${reduction}% smaller)`
      );
    } catch (error) {
      console.error('Error uploading craftie avatar:', error);
      alert('Failed to upload avatar');
    }
  };

  return (
    <>
      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        accept="image/jpeg,image/png,image/gif"
        onChange={handleFileChange}
      />
      <div
        className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300"
      >
        <button
          onClick={() => fileInputRef.current?.click()}
          className="text-white flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 px-3 py-2 rounded-full"
        >
          <Upload className="w-5 h-5" />
          <span className="text-sm">Change Avatar</span>
        </button>
      </div>
    </>
  );
};

const EditableInput = ({
  value,
  onSave,
  onCancel,
  type = 'text',
  placeholder = '',
  isTextarea = false
}: {
  value: string;
  onSave: (newValue: string) => void;
  onCancel: () => void;
  type?: string;
  placeholder?: string;
  isTextarea?: boolean;
}) => {
  const [inputValue, setInputValue] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (isTextarea) {
      textareaRef.current?.focus();
    } else {
      inputRef.current?.focus();
    }
  }, [isTextarea]);

  const handleSave = () => {
    if (inputValue.trim() !== '') {
      onSave(inputValue.trim());
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (isTextarea) {
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
        handleSave();
      } else if (e.key === 'Escape') {
        onCancel();
      }
    } else {
      if (e.key === 'Enter') {
        handleSave();
      } else if (e.key === 'Escape') {
        onCancel();
      }
    }
  };

  return (
    <div className="flex items-start space-x-2 w-full">
      {isTextarea ? (
        <textarea
          ref={textareaRef}
          value={inputValue}
          placeholder={placeholder}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          className="flex-grow px-2 py-1 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[80px]"
          rows={3}
        />
      ) : (
        <input
          ref={inputRef}
          type={type}
          value={inputValue}
          placeholder={placeholder}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          className="flex-grow px-2 py-1 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      )}
      <button
        onClick={handleSave}
        className="text-green-500 hover:bg-green-100 p-1 rounded-full mt-1"
      >
        <Check className="w-5 h-5" />
      </button>
      <button
        onClick={onCancel}
        className="text-red-500 hover:bg-red-100 p-1 rounded-full mt-1"
      >
        <X className="w-5 h-5" />
      </button>
    </div>
  );
};

const EditableField = ({
  label,
  value,
  icon: Icon,
  onEdit,
  isEditing,
  onSave,
  onCancel,
  type = 'text',
  placeholder = '',
  isTextarea = false
}: {
  label: string;
  value: string;
  icon: React.ComponentType<{ className?: string }>;
  onEdit: () => void;
  isEditing?: boolean;
  onSave?: (newValue: string) => void;
  onCancel?: () => void;
  type?: string;
  placeholder?: string;
  isTextarea?: boolean;
}) => {
  const [isHovered, setIsHovered] = useState(false);

  if (isEditing) {
    return (
      <div className="flex items-center text-gray-600 group relative w-full">
        <Icon className="w-5 h-5 mr-2 text-blue-600" />
        <EditableInput
          value={value}
          onSave={onSave!}
          onCancel={onCancel!}
          type={type}
          placeholder={placeholder}
          isTextarea={isTextarea}
        />
      </div>
    );
  }

  return (
    <div
      className="flex items-center text-gray-600 group relative w-full"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Icon className="w-5 h-5 mr-2 text-blue-600" />
      <span className="flex-grow">{value}</span>
      <button
        onClick={onEdit}
        className={`ml-2 p-1 rounded-full ${
          isHovered ? 'bg-blue-100 text-blue-600' : 'text-gray-400'
        } hover:bg-blue-100 hover:text-blue-600 transition-all duration-200 ease-in-out absolute right-0 top-1/2 -translate-y-1/2`}
      >
        <Edit2 className="w-4 h-4" />
      </button>
    </div>
  );
};

const CraftieDashboard = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [crafty, setCrafty] = useState<Craftsman | null>(null);
  const [loading, setLoading] = useState(true);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [debugInfo, setDebugInfo] = useState<any>({});
  const [isRegeneratingKeywords, setIsRegeneratingKeywords] = useState(false);

  const [craftieFeatures, setCraftieFeatures] = useState<TraderFeatures | null>(null);

  // Handle toggling map visibility for the craftie public profile
  const handleToggleMapVisibility = async () => {
    if (!crafty || typeof crafty.id === 'undefined') {
      console.error('No craftie data to update map visibility');
      return;
    }

    const current = (crafty as any).show_map ?? true;
    const next = !current;

    try {
      const updated = await craftsmanService.updateCraftsman(crafty.id as number, { show_map: next });
      if (updated) {
        setCrafty(updated);
        alert(`Map visibility ${next ? 'enabled' : 'disabled'} on your public profile.`);
      } else {
        console.error('Failed to update craftie map visibility: No data returned');
        alert('Failed to update map visibility');
      }
    } catch (err) {
      console.error('Error updating craftie map visibility:', err);
      alert('Failed to update map visibility');
    }
  };

  const fetchCraftieDetails = useCallback(async () => {
    if (authLoading) {
      return;
    }

    if (!user) {
      navigate('/login');
      setLoading(false);
      return;
    }

    try {
      const { data: { session } } = await supabase.auth.getSession();

      const craftyData = await craftsmanService.getCraftsmanByUserId(user.id);

      setDebugInfo({
        userEmail: user.email,
        userId: user.id,
        craftyDataReceived: !!craftyData,
        craftyDetails: craftyData,
        session
      });

      if (!craftyData) {
        setError('No craftie data found');
        navigate('/register/craftsman');
        setLoading(false);
        return;
      }

      setCrafty(craftyData);
      setError(null);
    } catch (err) {
      console.error('Error fetching craftie details:', err);
      setError('Failed to fetch craftie details');
    } finally {
      setLoading(false);
    }
  }, [user, authLoading, navigate]);

  const fetchCraftieFeatures = useCallback(async () => {
    const userId = user?.id || crafty?.user_id;

    if (!userId) {
      console.error('No user ID found for craftie features');
      return;
    }

    try {
      const features = await traderService.getTraderFeatures(userId);

      if (!features) {
        const defaultFeatures: TraderFeatures = {
          user_id: userId,
          feature1_enabled: true,
          feature1_title: crafty?.store_name || 'My Business',
          feature1_description: crafty?.description || 'Discover our unique offerings',
          feature1_image: crafty?.avatar_url || '/bally-Market-sign-200px.png',
          feature2_enabled: false,
          feature2_title: 'Orders',
          feature2_description: 'View and manage your orders',
          feature2_image: '/bally-Market-sign-200px.png',
          feature3_enabled: false,
          feature3_title: 'Analytics',
          feature3_description: 'View your sales and performance analytics',
          feature3_image: '/bally-Market-sign-200px.png'
        };
        setCraftieFeatures(defaultFeatures);
      } else {
        setCraftieFeatures(features);
      }
    } catch (error) {
      console.error('Error fetching craftie features:', error);
      setCraftieFeatures(null);
    }
  }, [user?.id, crafty?.user_id, crafty?.store_name, crafty?.description, crafty?.avatar_url]);

  useEffect(() => {
    if (!authLoading) {
      fetchCraftieDetails();
    }
  }, [fetchCraftieDetails, authLoading]);

  useEffect(() => {
    if (crafty) {
      setLoading(false);
    }
  }, [crafty]);

  useEffect(() => {
    if (crafty?.user_id) {
      fetchCraftieFeatures();
    }
  }, [crafty, fetchCraftieFeatures]);

  const [editingFeature, setEditingFeature] = useState<{
    key: string;
    title: string;
    description: string;
    enabled: boolean;
    image: string;
  } | null>(null);

  const [imageFile, setImageFile] = useState<File | null>(null);

  const [localFeatureToggles, setLocalFeatureToggles] = useState<{
    feature1_enabled: boolean;
    feature2_enabled: boolean;
    feature3_enabled: boolean;
  }>({
    feature1_enabled: craftieFeatures?.feature1_enabled || false,
    feature2_enabled: craftieFeatures?.feature2_enabled || false,
    feature3_enabled: craftieFeatures?.feature3_enabled || false
  });

  useEffect(() => {
    const initializeFeatureToggles = () => {
      if (!craftieFeatures) {
        return;
      }

      const initialToggles = {
        feature1_enabled: !!craftieFeatures.feature1_enabled,
        feature2_enabled: !!craftieFeatures.feature2_enabled,
        feature3_enabled: !!craftieFeatures.feature3_enabled
      };

      setLocalFeatureToggles(prev => {
        const shouldUpdate =
          prev.feature1_enabled !== initialToggles.feature1_enabled ||
          prev.feature2_enabled !== initialToggles.feature2_enabled ||
          prev.feature3_enabled !== initialToggles.feature3_enabled;

        if (shouldUpdate) {
          return initialToggles;
        }
        return prev;
      });
    };

    initializeFeatureToggles();
  }, [craftieFeatures]);

  const handleEditFeature = (feature: {
    key: keyof TraderFeatures;
    titleKey: keyof TraderFeatures;
    descKey: keyof TraderFeatures;
    enabledKey: keyof TraderFeatures;
    imageKey: keyof TraderFeatures;
    defaultTitle: string;
    defaultDesc: string;
    defaultImage: string;
  }) => {
    if (!craftieFeatures) return;

    setEditingFeature({
      key: feature.key,
      title: (craftieFeatures[feature.titleKey as keyof TraderFeatures] as string) || feature.defaultTitle,
      description: (craftieFeatures[feature.descKey as keyof TraderFeatures] as string) || feature.defaultDesc,
      image: (craftieFeatures[feature.imageKey as keyof TraderFeatures] as string) || feature.defaultImage,
      enabled: !!localFeatureToggles[feature.enabledKey as keyof typeof localFeatureToggles]
    });
  };

  const handleUpdateFeature = async () => {
    if (!editingFeature || !craftieFeatures) return;

    try {
      const updateData: Partial<TraderFeatures> = {
        [`${editingFeature.key}_title`]: editingFeature.title,
        [`${editingFeature.key}_description`]: editingFeature.description,
        [`${editingFeature.key}_enabled`]: editingFeature.enabled
      };

      if (imageFile) {
        try {
          const originalSize = imageFile.size;
          const compressedFile = await compressImage(imageFile, GALLERY_PRESET);
          const compressedSize = compressedFile.size;
          const reduction = originalSize > 0
            ? ((originalSize - compressedSize) / originalSize * 100).toFixed(0)
            : '0';

          const uploadPath = `trader_features/${craftieFeatures.user_id}/${editingFeature.key}_${Date.now()}.jpg`;

          const { data: uploadData, error: uploadError } = await supabase.storage
            .from('avatars')
            .upload(uploadPath, compressedFile, {
              cacheControl: '3600',
              upsert: true
            });

          if (uploadError) {
            console.error('Image upload error:', uploadError);
            alert('Failed to upload image');
            return;
          }

          const { data: urlData } = supabase.storage
            .from('avatars')
            .getPublicUrl(uploadPath);

          if (urlData) {
            updateData[`${editingFeature.key}_image`] = urlData.publicUrl;
            alert(
              `Feature image optimized: ${formatFileSize(originalSize)} → ${formatFileSize(compressedSize)} (${reduction}% smaller)`
            );
          }
        } catch (uploadError) {
          console.error('Unexpected image upload error:', uploadError);
          alert('Failed to upload image');
          return;
        }
      }

      const updatedFeatures = await traderService.updateTraderFeatures(
        craftieFeatures.user_id,
        updateData
      );

      if (updatedFeatures) {
        setCraftieFeatures(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            [`${editingFeature.key}_title`]: editingFeature.title,
            [`${editingFeature.key}_description`]: editingFeature.description,
            [`${editingFeature.key}_enabled`]: editingFeature.enabled,
            [`${editingFeature.key}_image`]: updateData[`${editingFeature.key}_image`] || prev[`${editingFeature.key}_image`],
            updated_at: new Date().toISOString()
          };
        });

        setLocalFeatureToggles(prev => ({
          ...prev,
          [`${editingFeature.key}_enabled`]: editingFeature.enabled
        }));

        setImageFile(null);
        setEditingFeature(null);
      } else {
        console.error('Failed to update feature: No data returned');
        alert('Failed to update feature');
      }
    } catch (error) {
      console.error('Unexpected error updating feature:', error);
      alert('An unexpected error occurred');
    }
  };

  const updateFeatureStatus = async (featureKey: string, newStatus: boolean) => {
    if (!craftieFeatures || !crafty) {
      console.error('Cannot update feature status: Missing craftie features or craftie data');
      return;
    }

    try {
      const updateData: Partial<TraderFeatures> = {
        [`${featureKey}_enabled`]: newStatus
      };

      setLocalFeatureToggles(prev => ({
        ...prev,
        [`${featureKey}_enabled`]: newStatus
      }));

      const updatedFeatures = await traderService.updateTraderFeatures(
        craftieFeatures.user_id,
        updateData
      );

      if (updatedFeatures) {
        setCraftieFeatures(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            [`${featureKey}_enabled`]: newStatus,
            updated_at: new Date().toISOString()
          };
        });
      } else {
        console.error('Failed to update feature status in database');
        setLocalFeatureToggles(prev => ({
          ...prev,
          [`${featureKey}_enabled`]: !newStatus
        }));
        alert('Failed to update feature status');
      }
    } catch (error) {
      console.error('Unexpected error updating feature status:', error);
      setLocalFeatureToggles(prev => ({
        ...prev,
        [`${featureKey}_enabled`]: !newStatus
      }));
      if (error instanceof Error) {
        alert(`Failed to update feature status: ${error.message}`);
      } else {
        alert('An unexpected error occurred');
      }
    }
  };

  const renderCraftieFeatures = () => {
    if (!craftieFeatures) {
      return null;
    }

    const featuresToRender = [
      {
        key: 'feature1',
        label: 'Feature 1',
        enabledKey: 'feature1_enabled',
        titleKey: 'feature1_title',
        descKey: 'feature1_description',
        imageKey: 'feature1_image',
        defaultTitle: crafty?.store_name || 'My Business',
        defaultDesc: crafty?.description || 'Discover our unique offerings',
        defaultImage: crafty?.avatar_url || '/bally-Market-sign-200px.png'
      },
      {
        key: 'feature2',
        label: 'Feature 2',
        enabledKey: 'feature2_enabled',
        titleKey: 'feature2_title',
        descKey: 'feature2_description',
        imageKey: 'feature2_image',
        defaultTitle: 'Orders',
        defaultDesc: 'View and manage your orders',
        defaultImage: '/bally-Market-sign-200px.png'
      },
      {
        key: 'feature3',
        label: 'Feature 3',
        enabledKey: 'feature3_enabled',
        titleKey: 'feature3_title',
        descKey: 'feature3_description',
        imageKey: 'feature3_image',
        defaultTitle: 'Analytics',
        defaultDesc: 'View your sales and performance analytics',
        defaultImage: '/bally-Market-sign-200px.png'
      }
    ];

    return (
      <>
        <div className="hidden sm:grid sm:grid-cols-3 gap-4 mb-4 mt-6">
          {featuresToRender.map((feature) => {
            const isEnabled = !!localFeatureToggles[feature.enabledKey as keyof typeof localFeatureToggles];

            return (
              <div key={feature.key} className="flex items-center space-x-2 justify-between sm:justify-start">
                <span className="text-sm font-semibold text-gray-600">{feature.label}</span>
                <input
                  type="checkbox"
                  checked={isEnabled}
                  onChange={() => {
                    const newEnabledState = !isEnabled;
                    updateFeatureStatus(feature.key, newEnabledState);
                  }}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <button
                  onClick={() => handleEditFeature(feature as any)}
                  className="text-gray-500 hover:text-blue-600"
                  title="Edit Feature"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                  </svg>
                </button>
              </div>
            );
          })}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {featuresToRender.map((feature) => {
            const isEditing = editingFeature?.key === feature.key;
            const isEnabled = !!localFeatureToggles[feature.enabledKey as keyof typeof localFeatureToggles];
            const title = (craftieFeatures[feature.titleKey as keyof TraderFeatures] as string) || feature.defaultTitle;
            const description = (craftieFeatures[feature.descKey as keyof TraderFeatures] as string) || feature.defaultDesc;
            const imageUrl = (craftieFeatures[feature.imageKey as keyof TraderFeatures] as string) || feature.defaultImage;

            return (
              <div
                key={feature.key}
                className={`bg-blue-50 p-6 rounded-lg relative ${!isEnabled ? 'opacity-50' : ''}`}
              >
                <div className="sm:hidden flex items-center justify-between mb-4">
                  <span className="text-sm font-semibold text-gray-600">{feature.label}</span>
                  <input
                    type="checkbox"
                    checked={isEnabled}
                    onChange={() => {
                      const newEnabledState = !isEnabled;
                      updateFeatureStatus(feature.key, newEnabledState);
                    }}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                </div>

                {isEditing && editingFeature ? (
                  <div className="space-y-4">
                    <div className="flex justify-center mb-4">
                      <img
                        src={
                          imageFile
                            ? URL.createObjectURL(imageFile)
                            : editingFeature.image || feature.defaultImage
                        }
                        alt={`${editingFeature.title} Icon`}
                        className="h-32 w-32 object-contain rounded-lg"
                        onError={(e) => {
                          const imgEl = e.target as HTMLImageElement;
                          imgEl.src = feature.defaultImage;
                        }}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Feature Title</label>
                      <input
                        type="text"
                        value={editingFeature.title}
                        onChange={(e) =>
                          setEditingFeature(prev => (prev ? { ...prev, title: e.target.value } : null))
                        }
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Feature Description</label>
                      <textarea
                        value={editingFeature.description}
                        onChange={(e) =>
                          setEditingFeature(prev => (prev ? { ...prev, description: e.target.value } : null))
                        }
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                        rows={3}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Feature Image</label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            if (file.size > 5 * 1024 * 1024) {
                              alert('File is too large. Maximum size is 5MB.');
                              e.target.value = '';
                              return;
                            }
                            setImageFile(file);
                          }
                        }}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                      />
                    </div>
                    <div className="flex justify-between mt-4">
                      <button
                        onClick={handleUpdateFeature}
                        className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
                      >
                        Save
                      </button>
                      <button
                        onClick={() => {
                          setEditingFeature(null);
                          setImageFile(null);
                        }}
                        className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex justify-center mb-4">
                      <img
                        src={imageUrl}
                        alt={`${title} Icon`}
                        className="h-16 w-16 object-contain"
                        onError={(e) => {
                          const imgEl = e.target as HTMLImageElement;
                          imgEl.src = feature.defaultImage;
                        }}
                      />
                    </div>
                    <h3 className="font-semibold mb-2 text-center">{title}</h3>
                    <p className="text-gray-600 text-center">{description}</p>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </>
    );
  };

  if (authLoading || loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (error || !crafty) {
    return (
      <div className="flex justify-center items-center h-screen flex-col p-4 text-center">
        <div className="text-red-500 text-xl mb-4">{error || 'Unable to load craftie data'}</div>
        <button
          onClick={() => navigate('/register/craftsman')}
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 mb-4"
        >
          Go to Registration
        </button>
        <details className="w-full max-w-md bg-gray-100 rounded p-4 text-left">
          <summary className="cursor-pointer text-gray-700">Debug Information</summary>
          <pre className="text-xs overflow-x-auto">{JSON.stringify(debugInfo, null, 2)}</pre>
        </details>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-center mb-4">
        <Link to="/">
          <img
            src="/bally-Market-sign-200px.png"
            alt="Ballyconnell Market Logo"
            className="h-12 w-auto cursor-pointer"
          />
        </Link>
      </div>
      <h1 className="text-2xl font-bold mb-6">Craftie Dashboard</h1>
      <div className="bg-white rounded-lg shadow p-6">
        <div className="mb-6 flex items-center space-x-4">
          {crafty.avatar_url ? (
            <div className="relative w-20 h-20 group">
              <img
                src={crafty.avatar_url}
                alt={`${crafty.store_name} avatar`}
                className="w-20 h-20 rounded-full object-cover border-4 border-blue-50 shadow-md"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                    crafty.store_name
                  )}&background=random`;
                }}
              />
              <AvatarUpload
                crafty={crafty}
                onAvatarUpdate={(newAvatarUrl) => {
                  setCrafty((prev) => (prev ? { ...prev, avatar_url: newAvatarUrl } : null));
                }}
              />
            </div>
          ) : (
            <div className="w-20 h-20 rounded-full bg-gray-200 flex items-center justify-center text-gray-500">
              <img
                src={`https://ui-avatars.com/api/?name=${encodeURIComponent(
                  crafty.store_name
                )}&background=random`}
                alt={`${crafty.store_name} avatar`}
                className="w-full h-full rounded-full object-cover"
              />
            </div>
          )}
          <div>
            <h2 className="text-lg font-semibold mb-1">
              Welcome, {crafty.store_name || user?.user_metadata?.name || 'Craftie'}
            </h2>
            <p className="text-gray-600">Manage your craft store and orders here.</p>
          </div>
        </div>

        <div className="mb-6 bg-gray-50 p-4 rounded-lg">
          <h3 className="text-md font-semibold mb-3">Your Business Details</h3>
          <div className="space-y-2">
            <EditableField
              label="Business Name"
              value={crafty.store_name}
              icon={Store}
              onEdit={() => setEditingField('store_name')}
              isEditing={editingField === 'store_name'}
              onSave={async (value) => {
                if (!crafty.id) return;
                try {
                  const updated = await craftsmanService.updateCraftsman(crafty.id, { store_name: value });
                  if (updated) {
                    setCrafty(updated);
                    setEditingField(null);
                  }
                } catch (err) {
                  console.error('Failed to update business name', err);
                  alert('Failed to update business name');
                }
              }}
              onCancel={() => setEditingField(null)}
              placeholder="Enter business name"
            />

            <EditableField
              label="Owner Name"
              value={crafty.owner_name}
              icon={User}
              onEdit={() => setEditingField('owner_name')}
              isEditing={editingField === 'owner_name'}
              onSave={async (value) => {
                if (!crafty.id) return;
                try {
                  const updated = await craftsmanService.updateCraftsman(crafty.id, { owner_name: value });
                  if (updated) {
                    setCrafty(updated);
                    setEditingField(null);
                  }
                } catch (err) {
                  console.error('Failed to update owner name', err);
                  alert('Failed to update owner name');
                }
              }}
              onCancel={() => setEditingField(null)}
              placeholder="Enter owner name"
            />

            {crafty.phone_number && (
              <EditableField
                label="Phone Number"
                value={crafty.phone_number}
                icon={Phone}
                onEdit={() => setEditingField('phone_number')}
                isEditing={editingField === 'phone_number'}
                onSave={async (value) => {
                  if (!crafty.id) return;
                  try {
                    const updated = await craftsmanService.updateCraftsman(crafty.id, { phone_number: value });
                    if (updated) {
                      setCrafty(updated);
                      setEditingField(null);
                    }
                  } catch (err) {
                    console.error('Failed to update phone number', err);
                    alert('Failed to update phone number');
                  }
                }}
                onCancel={() => setEditingField(null)}
                type="tel"
                placeholder="Enter phone number"
              />
            )}

            <EditableField
              label="Website"
              value={crafty.website || 'No website added'}
              icon={Globe}
              onEdit={() => setEditingField('website')}
              isEditing={editingField === 'website'}
              onSave={async (value) => {
                if (!crafty.id) return;
                try {
                  const updated = await craftsmanService.updateCraftsman(crafty.id, { website: value });
                  if (updated) {
                    setCrafty(updated);
                    setEditingField(null);
                  }
                } catch (err) {
                  console.error('Failed to update website', err);
                  alert('Failed to update website');
                }
              }}
              onCancel={() => setEditingField(null)}
              type="url"
              placeholder="https://your-website.com"
            />

            {crafty.location && (
              <EditableField
                label="Location"
                value={crafty.location}
                icon={MapPin}
                onEdit={() => setEditingField('location')}
                isEditing={editingField === 'location'}
                onSave={async (value) => {
                  if (!crafty.id) return;
                  try {
                    const updated = await craftsmanService.updateCraftsman(crafty.id, { location: value });
                    if (updated) {
                      setCrafty(updated);
                      setEditingField(null);
                    }
                  } catch (err) {
                    console.error('Failed to update location', err);
                    alert('Failed to update location');
                  }
                }}
                onCancel={() => setEditingField(null)}
                placeholder="Enter location"
              />
            )}

            {crafty.description && (
              <EditableField
                label="Description"
                value={crafty.description}
                icon={Info}
                onEdit={() => setEditingField('description')}
                isEditing={editingField === 'description'}
                onSave={async (value) => {
                  if (!crafty.id) return;
                  try {
                    const updated = await craftsmanService.updateCraftsman(crafty.id, { description: value });
                    if (updated) {
                      setCrafty(updated);
                      setEditingField(null);
                      alert('Description updated successfully!');
                    }
                  } catch (err) {
                    console.error('Failed to update description', err);
                    alert('Failed to update description');
                  }
                }}
                onCancel={() => setEditingField(null)}
                placeholder="Enter description"
                isTextarea={true}
              />
            )}

            <div className="mt-4 pt-4 border-t border-gray-200">
              <button
                onClick={async () => {
                  if (!crafty || !crafty.id) return;

                  setIsRegeneratingKeywords(true);
                  try {
                    const success = await triggerKeywordGeneration(crafty.id, 'craftie', {
                      store_name: crafty.store_name,
                      description: crafty.description,
                      location: crafty.location
                    });

                    if (success) {
                      alert('Keyword generation started! Keywords will be updated in a few moments.');
                    } else {
                      alert('Failed to trigger keyword generation. Please try again.');
                    }
                  } catch (err) {
                    console.error('Error triggering keyword generation for craftie:', err);
                    alert('Error triggering keyword generation');
                  } finally {
                    setIsRegeneratingKeywords(false);
                  }
                }}
                disabled={isRegeneratingKeywords}
                className={`
                  flex items-center justify-center gap-2 w-full px-4 py-2 
                  bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg
                  hover:from-blue-600 hover:to-blue-700
                  disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed
                  transition-all duration-200 shadow-md hover:shadow-lg
                  ${isRegeneratingKeywords ? 'opacity-75' : ''}
                `}
              >
                <RefreshCw className={`w-4 h-4 ${isRegeneratingKeywords ? 'animate-spin' : ''}`} />
                <span>{isRegeneratingKeywords ? 'Generating Keywords...' : 'Regenerate Search Keywords'}</span>
              </button>
              <p className="text-xs text-gray-500 mt-2 text-center">
                Click to update search keywords based on your current business information
              </p>
            </div>
          </div>

          <div className="mt-6">
            <SocialLinksCard />
          </div>

          {hasLocationForMap && (
            <div className="mt-6 bg-gray-50 p-4 rounded-lg">
              <h3 className="text-md font-semibold mb-3">Location Map</h3>
              <div className="flex items-center justify-between text-sm text-gray-600 mb-3">
                <span>Show map on your public profile</span>
                <button
                  type="button"
                  onClick={handleToggleMapVisibility}
                  className={`px-3 py-1 rounded-full border text-xs font-medium ${
                    ((crafty as any).show_map ?? true)
                      ? 'bg-green-50 text-green-700 border-green-200'
                      : 'bg-gray-50 text-gray-500 border-gray-200'
                  }`}
                >
                  {((crafty as any).show_map ?? true) ? 'On' : 'Off'}
                </button>
              </div>
              {mapUrl && (
                <div className="w-full overflow-hidden rounded-lg border border-gray-200">
                  <iframe
                    title={`Map preview for ${crafty.store_name}`}
                    src={mapUrl}
                    width="100%"
                    height="260"
                    style={{ border: 0 }}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
              )}
              {!mapUrl && hasLocationForMap && (
                <p className="mt-2 text-xs text-gray-500">
                  Map is hidden on your public profile because visibility is turned off above.
                </p>
              )}
            </div>
          )}

          <div className="mt-8 border-t border-gray-200 pt-6">
            <h3 className="text-md font-semibold mb-4">Feature Cards</h3>
            {renderCraftieFeatures()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CraftieDashboard;
